<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';
  import { registerFocus } from '../focus-manager/focus.action.svelte.js';
  import type { FocusItem, FocusLayer } from '../focus-manager/focus-types';
  import { getCodeFromEvent, isKeyCombination } from '../utils/keyboard-event';
  import type { SliderOrientation, SliderState } from '../types/slider';

  /**
   * The lowest value in the range of permitted values. It must be less than the
   * max value.
   */
  export let min = 0;

  /**
   * The greatest value in the range of permitted values. It must be greater
   * than the min value.
   */
  export let max = 100;

  /**
   * A number that specifies how the current value will change when stepping
   * through, for example with the arrow keys. It must be greater than 0.
   */
  export let step = 1;

  /**
   * A number that specifies how the current value will change when stepping
   * through multiple steps at once, for example with the PageUp and PageDown
   * keys
   */
  export let multiStep = 10 * step;

  /**
   * The currently selected number. It must be greater than or equal to the min
   * value and less than or equal to the max value.
   */
  export let current = 0;

  /**
   * Indicates whether the slider's orientation is horizontal or vertical.
   */
  export let orientation: SliderOrientation = 'horizontal';

  /**
   * Whether the slider should be read-only.
   * If it's read-only, the knob is visible and shows the current value, but the
   * user cannot interact with it.
   */
  export let readonly = false;

  /**
   * Whether the slider should be presentational only. The track will be
   * displayed, but the knob will be not. The slider is not available for
   * assistive technology.
   */
  export let presentationalOnly = false;

  /**
   * A descriptive label for the slider.
   */
  export let label: string;

  /**
   * A callback function to format the current value in a human-understandable
   * way when reading out, for example in a screen reader.
   *
   * The callback will receive 3 values: current, min and max.
   */
  export let getValueText: (current: number, min: number, max: number) => string = () => '';

  interface EventMap {
    /** Emitted when the mouse enters the slider.
     * The value and x coordinate denote the mouse position and the value at
     * that position. The slider value will not be updated on mouse enter. */
    startHovering: SliderState;

    /** Emitted when the user hovers the slider (mousemove).
     * The value and x coordinate denote the mouse position and the value at
     * that position. The slider value will not be updated while hovering. */
    hovering: SliderState;

    /** Emitted when the mouse leaves the slider. */
    stopHovering: undefined;

    /** Emitted when the user activates the slider knob (mousedown or
     * touchstart) or when the user presses one of the keys that can control
     * the slider.
     * The value and x coordinate are of the current value. Mousedown or
     * touchstart will not update the slider value. */
    startSliding: SliderState;

    /** Emitted when the user moves the slider knob (mousemove, touchmove, via
     * keyboard interaction). The slider value gets updated by the movement.
     * Will also be called when the slider value is updated programmatically
     * (that is when the `current` prop changes. */
    change: SliderState;

    /** Emitted when the user releases the slider knob (mouseup or touchend) or
     * when the user released one of the keys that can control the slider.
     * The value and x coordinate are of the current value. */
    stopSliding: SliderState;
  }

  const dispatch = createEventDispatcher<EventMap>();

  const dispatchSliderState = (event: keyof EventMap, current: number, px?: number) => {
    dispatch(event, {
      value: current,
      size: sliderSize,
      px: px ?? getKnobPx(),
    });
  };

  // A reference to the slider, needed to get the size
  let sliderElement: HTMLElement;

  // The current size of the slider (width for horizontal orientation, height
  // otherwise)
  let sliderSize: number;

  // The position of the knob within the bar, seen from the "low" end of the
  // slider (left resp. bottom)
  $: offsetInPercent = (100 * (current - min)) / (max - min);

  // When in vertical orientation, we have to multiply some values by -1 because
  // higher values have a lower y position, whereas horizontally, higher values
  // have a higher x position;
  $: axisFactor = orientation === 'horizontal' ? 1 : -1;

  $: {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (sliderElement && sliderSize) {
      // be aware: this will always dispatch if the `current` property changes
      // (including property changes passed by the parent component)
      dispatchSliderState('change', current, getKnobPx());
    }
  }

  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  onMount(() => {
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver((entries) => {
        const property = orientation === 'horizontal' ? 'width' : 'height';
        sliderSize = entries[0].contentRect[property];
      });

      resizeObserver.observe(sliderElement);
      return () => resizeObserver.unobserve(sliderElement);
    } else {
      sliderSize = orientation === 'horizontal' ? sliderElement.offsetWidth : sliderElement.offsetHeight;
    }
  });

  /** Get the event coordinate in relation to the viewport */
  const getClientPosition = (event: MouseEvent | TouchEvent) => {
    const property = orientation === 'horizontal' ? 'clientX' : 'clientY';
    return 'touches' in event ? event.touches[0][property] : event[property];
  };

  /** Get the event coordinate in relation to the slider element */
  const getOffsetPosition = (event: MouseEvent | TouchEvent) => {
    const clientPosition = getClientPosition(event);
    const { left, top } = sliderElement.getBoundingClientRect();
    const offset = orientation === 'horizontal' ? left : top;

    // limit the result to the slider dimensions
    return Math.max(0, Math.min(sliderSize, clientPosition - offset));
  };

  /** Get the knob px coordinate in relation to the slider element or viewport. */
  const getKnobPx = (reference: 'viewport' | 'slider' = 'slider') => {
    const { height, left, top, width } = sliderElement.getBoundingClientRect();
    const size = orientation === 'horizontal' ? width : height;
    const offset = orientation === 'horizontal' ? left : top;

    const offsetToReference = reference === 'viewport' ? offset : 0;
    const offsetInPercentOnAxis = orientation === 'horizontal' ? offsetInPercent : 100 - offsetInPercent;

    // We calculate the offset in percent from the actual value. Here we
    // calculate the pixel value from the offset in percent. Numeric errors
    // may occur, so we round the value to get a consistent experience.
    return Math.round((offsetInPercentOnAxis * size) / 100 + offsetToReference);
  };

  /** Calculates the value (within the range of min to max) with respect to the
   * step, based on the given x or y pixel position within the slider. */
  const getValueAtPosition = (position: number) => {
    const currentKnobPx = getKnobPx();
    const deltaPx = position - currentKnobPx;
    const deltaValue = axisFactor * (deltaPx / sliderSize) * (max - min);

    return current + Math.round(deltaValue / step) * step;
  };

  /*
    How we handle dragging and keyboard handling:

    The slider has its root element (slider), a bar (from min to max), a track
    (from min to current) and a knob (at the current position).

    From an accessibility perspective, the knob is the slider. It has all the
    information needed for assistive technology such as the min value, the max
    value and the current value, as well as the role of "slider".

    This component accepts properties (amongst others) min, max, current. From
    these values, it calculates the position of the knob in percent. This way,
    when the size of the slider changes, we don't have to recalculate the
    position of the knob.

    However, when dragging the slider, we get the pointer coordinates in pixels.
    Therefore, we have to know the slider width while dragging, to calculate the
    new value from the width and the pointer coordinates.

    When the user starts dragging, they most likely won't have the mouse at the
    exact center position of the knob. So we have to store the offset of the
    mouse to the center position, to keep that in mind when calculating the new
    value.

    According to WCAG, we do not update the value on mouse or touch down, but
    only on mouse or touch move.

    Therefore, the order of action is:

    1. mouse down/touch start: Get and store the coordinates of the pointer and
       the start value. Emit a startSliding event.
    2. mouse/touch move: Get the delta of the movement and calculate the new
       value from it. Emit a change event.
    3. mouse up/touch end: Emit a stopSliding event.

    A click on the bar should also change the slider to this value. We can see a
    click as a mouse down at the current position, a mouse move to the mouse
    position of the click and a mouse up at that position. By re-using the mouse
    events, we make sure to also emit the events in the same way.

    Handling the keyboard is simpler. We don't have to calculate anything from
    mouse positions or the slider width. We simply add or subtract a step from
    the current value.

    For keyboard users, we map the key handling to that of mouse/touch handling:

    1. key down: Emit a startSliding event. Do not change the value
    2. continued key down: Update the slider value and emit a change event.
    3. key up: Update the slider value and emit a stopSliding event.

    By re-using the same events as for mouse or touch, the integrator can handle
    all three input methods as one. For example, they can show thumbnails for
    mouse/touch move as well as continued key pressed. They can update the value
    on mouse up/touch end/key up.
 */

  //
  // Sliding using Mouse or Touch
  //

  /** Whether we're in the process of sliding, that is the user has the mouse or
   * touch down */
  let sliding = false;

  /** To calculate the new value depending on the pointer movement, we have to
   * save the state when sliding starts. */
  let slidingStartState: {
    value: number;
    pointerPosition: number;
  } = { value: 0, pointerPosition: 0 };

  const startSliding = (event: MouseEvent | TouchEvent) => {
    // prevent scrolling while dragging the slider
    event.preventDefault();

    sliding = true;
    dispatchSliderState('startSliding', current);

    slidingStartState.value = current;
    slidingStartState.pointerPosition = getClientPosition(event);
  };

  const updateSliderOnMove = (event: MouseEvent | TouchEvent) => {
    if (sliding) {
      // prevent scrolling while dragging the slider
      event.preventDefault();

      const clientPosition = getClientPosition(event);
      const deltaPx = axisFactor * (clientPosition - slidingStartState.pointerPosition);
      const deltaValue = (deltaPx / sliderSize) * (max - min);
      const value = slidingStartState.value + Math.round(deltaValue / step) * step;

      current = Math.max(min, Math.min(max, value));
    }
  };

  const stopSliding = () => {
    sliding = false;
    dispatchSliderState('stopSliding', current);
  };

  // Whether the user can interact with the slider. False when readonly or
  // presentational.
  $: interactive = !(readonly || presentationalOnly);

  $: {
    // If the slider gets readonly/disabled while sliding, stop the sliding.
    // We don't have to stop hovering when the slider gets readonly/disabled,
    // because maybe the parent component still wants to act on the mouse
    // movement, even when readonly/disabled.
    if (!interactive && sliding) {
      stopSliding();
    }
  }

  //
  // Handling of hover
  //

  const dispatchStartHovering = (event: MouseEvent) => {
    // Do not use offsetX/offsetY of the event directly, because it relates to
    // the event target, not the element the event was registered on.
    const position = getOffsetPosition(event);
    const valueAtPosition = getValueAtPosition(position);

    dispatchSliderState('startHovering', valueAtPosition, position);
  };

  const dispatchHoveringInformation = (event: MouseEvent) => {
    // Do not use offsetX/offsetY of the event directly, because it relates to
    // the event target, not the element, the event was registered on.
    const position = getOffsetPosition(event);
    const valueAtPosition = getValueAtPosition(position);

    dispatchSliderState('hovering', valueAtPosition, position);
  };

  const dispatchStopHovering = () => {
    dispatch('stopHovering');
  };

  //
  // Clicking or tapping anywhere on the slider
  //

  const setSliderOnClick = (event: MouseEvent) => {
    const knobPx = getKnobPx('viewport');
    const property = orientation === 'horizontal' ? 'clientX' : 'clientY';

    const startSlidingEvent = new MouseEvent('mousedown', {
      [property]: knobPx,
    });

    // A click on the bar can be seen as a mousedown at the current knob
    // position, a move to the click position and a mouse up
    startSliding(startSlidingEvent);
    updateSliderOnMove(event);
    stopSliding();
  };

  //
  // Handling the keyboard
  //

  /** A list of all keys that can change the slider state */
  const sliderKeys = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageDown', 'PageUp']);

  const isSliderKey = (event: KeyboardEvent): boolean => {
    const code = getCodeFromEvent(event);
    return sliderKeys.has(code);
  };

  const updateSlider = (event: KeyboardEvent) => {
    let nextValue: number | undefined;
    const code = getCodeFromEvent(event);

    switch (code) {
      case 'ArrowLeft':
      case 'ArrowDown':
        nextValue = current - step;
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        nextValue = current + step;
        break;
      case 'Home':
        nextValue = min;
        break;
      case 'End':
        nextValue = max;
        break;
      case 'PageDown':
        nextValue = current - multiStep;
        break;
      case 'PageUp':
        nextValue = current + multiStep;
        break;
    }

    if (typeof nextValue === 'number') {
      current = Math.max(min, Math.min(max, nextValue));
    }
  };

  const updateSliderOnKeyDown = (event: KeyboardEvent) => {
    // do not handle events with key combinations, such as Alt + ArrowLeft
    // (which usually navigates back in history)
    if (isKeyCombination(event) || !isSliderKey(event)) {
      return;
    }

    event.preventDefault();

    // When keeping the key pressed, the browser fires the keydown event
    // repeatedly. We can detect this by the `repeated` flag of the event.
    // When it is true, we can handle the event the same way as a mouse/touch
    // move event.
    if (event.repeat) {
      updateSlider(event);
    } else {
      sliding = true;
      dispatchSliderState('startSliding', current);
    }
  };

  const updateSliderOnKeyUp = (event: KeyboardEvent) => {
    // do not handle events with key combinations, such as Alt + ArrowLeft
    // (which usually navigates back in history)
    if (isKeyCombination(event) || !isSliderKey(event)) {
      return;
    }

    event.preventDefault();

    updateSlider(event);
    sliding = false;
    dispatchSliderState('stopSliding', current);
  };
</script>

<svelte:body
  on:mousemove={interactive && sliding ? updateSliderOnMove : null}
  on:mouseup={interactive && sliding ? stopSliding : null}
  on:mouseleave={interactive && sliding ? stopSliding : null}
  on:touchmove={interactive && sliding ? updateSliderOnMove : null}
  on:touchend={interactive && sliding ? stopSliding : null}
  on:touchcancel={interactive && sliding ? stopSliding : null}
/>
<!-- svelte-ignore a11y-no-static-element-interactions a11y-click-events-have-key-events -->
<div
  class={$$props.class}
  class:slider={true}
  class:disabled={!interactive}
  class:vertical={orientation === 'vertical'}
  data-testid={$$props.id || 'Slider'}
  bind:this={sliderElement}
  on:click={interactive ? setSliderOnClick : null}
  on:mouseenter={interactive ? dispatchStartHovering : null}
  on:mousemove={interactive ? dispatchHoveringInformation : null}
  on:mouseleave={interactive ? dispatchStopHovering : null}
>
  <div class="bar">
    <div class="track" style="{orientation === 'horizontal' ? 'width' : 'height'}: {offsetInPercent}%" />
    {#if !presentationalOnly}
      <div
        class="knob"
        tabindex="0"
        role="slider"
        title={label}
        style="{orientation === 'horizontal' ? 'left' : 'bottom'}: {offsetInPercent}%"
        aria-orientation={orientation}
        aria-readonly={readonly}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={current}
        aria-valuetext={getValueText(current, min, max)}
        on:mousedown={readonly ? null : startSliding}
        on:keydown={readonly ? null : updateSliderOnKeyDown}
        on:keyup={readonly ? null : updateSliderOnKeyUp}
        on:touchstart={readonly ? null : startSliding}
        use:registerFocus={{ focusLayer, focusName }}
        data-focusName={focusName}
        data-focusLayer={focusLayer}
      />
    {/if}
  </div>
</div>

<style module>
  .slider {
    width: 100%;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    /* Increase the click area */
    padding: 8px 0;
    /* Min value (4px) ends at 400 px. Max value (8px) starts at 800 px. */
    /* https://modern-fluid-typography.vercel.app/ */
    padding: clamp(4px, 1cqw, 8px) 0;

    user-select: none;
  }

  .slider.vertical {
    height: 100%;
    width: 24px;

    padding: 0 8px;
    padding: 0 clamp(4px, 1cqw, 8px);
  }

  .disabled {
    cursor: default;
  }

  .bar {
    position: relative;
    width: 100%;
    height: 4px;
    height: clamp(4px, 0.25em, 8px);
    border-radius: 8px;

    background-color: #4c4c4c; /* Fallback for older browsers */
    background-color: var(--slider-bar-color);
  }

  .slider.vertical .bar {
    height: 100%;
    width: 4px;
    width: clamp(4px, 0.25em, 8px);
    display: flex;
    flex-direction: column-reverse;
  }

  .track {
    height: 100%;
    max-width: 100%;
    border-radius: 8px;

    background-color: currentColor; /* Fallback for older browsers */
    background-color: var(--slider-track-color, #ffffff);
  }

  .slider.vertical .track {
    width: 100%;
    max-height: 100%;
  }

  .knob {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    background-color: #f5f5f5;
    background-color: var(--slider-knob-color);
    background-clip: content-box;

    box-sizing: border-box;
    width: 24px;
    height: 24px;

    padding: 2px;
    /* The bar scales, and so does the knob, but to a limit */
    /* min-size: 10x10px (=> 7px padding), max-size: 20x20px (=> 2px padding) */
    padding: max(2px, (16px + 7px) - 1em);

    outline-offset: 2px;
  }

  .slider.vertical .knob {
    top: initial;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  /* we want to keep the information of the slider/knob in the a11y-tree, therefore
  we don't hide the knob or use properties like "display: none" or "disabled" */
  .knob[aria-readonly='true'] {
    position: absolute;
    height: 1px;
    width: 1px;
    overflow: hidden;
    white-space: nowrap;
    opacity: 0;
  }

  .slider:hover .bar {
    background-color: #4c4c4c;
    background-color: var(--slider-hover-bar-color);
  }

  .slider:hover .track {
    background-color: #f0f0f0;
    background-color: var(--slider-hover-track-color);
  }

  .slider:hover .knob,
  .knob:hover {
    background-color: #ffffff;
    background-color: var(--slider-hover-knob-color);
  }

  /* Simulate a round outline by adding a pseudo-element */
  .knob:focus::after {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    width: 100%;
    height: 100%;
    box-sizing: content-box;
    border: 2px solid #ffffff;
    border-radius: 50%;
  }

  @supports selector(:focus-visible) {
    /* Don't show the outline on focus... */
    .knob:focus::after {
      content: none;
    }

    /* ...but show it on focus-visible */
    .knob:focus-visible {
      outline: 2px solid #ffffff; /* Fallback for older browsers */
      outline: var(--outline);
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    .bar,
    .track,
    .knob {
      transition: background-color 0.3s ease-in-out;
    }
  }

  /* display a bigger knob on devices with a coarse pointer device (like a touchscreen) */
  @media (pointer: coarse) {
    .knob {
      padding: 2px;
    }
  }

  @media (prefers-contrast: more) {
    .slider:hover .knob {
      outline: var(--outline);
    }
  }
</style>
